import { cloneElement, useMemo } from 'react';
import { css } from '@emotion/react';
import { motion, AnimatePresence } from 'motion/react';
import { theme } from '@frontend/theme';
import { mergeClasses } from '../../../helpers';
import { useThemeValues } from '../../../hooks';
import { ContentLoader } from '../../loader';
import { ModalBoxProps } from '../atoms';
import { useModalContext } from '../provider';
import { useResponsiveModal } from './responsive-modal.context';

const maxWidthIncludingMargins = '100vw - 48px';

/**
 * Equivalent to ModalBox when !isResponsiveMode
 * Otherwise, equivalent to full screen / mobile.
 */
export const ResponsiveModalBox = ({
  children,
  asChild,
  className,
  flex,
  minWidth = 0,
  maxWidth = 0,
  ...rest
}: ModalBoxProps) => {
  const { containerProps, loadingState } = useModalContext();
  const { spacing, colors, borderRadius } = useThemeValues();
  const { isResponsiveMode } = useResponsiveModal();

  const getStyles = () => {
    if (isResponsiveMode) {
      /**
       * Mobile modal styles
       */
      return css`
        width: 100%;
        height: 100%;
        background: white;
        display: flex;
        flex-direction: column;
        overflow: auto;
        border-radius: ${borderRadius.medium};
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        :focus {
          outline: none;
        }
        .wv-modal-header {
          padding: ${spacing(2, 3)};
        }
        .wv-modal-footer {
          padding: ${spacing(2, 3)};
        }
      `;
    }

    /**
     * Desktop modal styles
     */
    return [
      css`
        width: ${maxWidth ? '100%' : 'unset'};
        min-width: ${minWidth > 0 ? `min(${minWidth}px, ${maxWidthIncludingMargins})` : 0};
        max-width: ${maxWidth
          ? `min(${maxWidth}px, ${maxWidthIncludingMargins})`
          : `calc(${maxWidthIncludingMargins})`};
        max-height: calc(100vh - ${spacing(8)});
        background: ${colors.white};
        border-radius: ${borderRadius.medium};
        position: relative;
        padding: ${spacing(3, 0)};

        > {
          // All the scrollbars within the modal should be consistent
          ::-webkit-scrollbar {
            width: 6px;
            height: 6px;
          }

          ::-webkit-scrollbar-thumb {
            background-color: ${theme.colors.neutral20};
            border-radius: ${borderRadius.small};
          }
        }
      `,
      flex
        ? css`
            display: grid;
            > * {
              // These make grid cells work as expected, otherwise grid sets them to be auto
              min-width: 0;
              min-height: 0;
            }

            // Body only
            :has(.wv-modal-flex-content):not(:has(> .wv-modal-header, > .wv-modal-label, > .wv-modal-footer)) {
              grid-template-rows: [body-start] minmax(0, 1fr) [body-end];
            }

            // Body and Footer
            :has(.wv-modal-flex-content + .wv-modal-footer):not(:has(> .wv-modal-header, > .wv-modal-label)) {
              grid-template-rows: [body-start] minmax(0, 1fr) [body-end] ${spacing(3)} [footer-start] auto [footer-end];
            }

            // Header and Body
            :has(.wv-modal-header + .wv-modal-flex-content):not(:has(> .wv-modal-label, > .wv-modal-footer)) {
              grid-template-rows: [header-start] auto [header-end] ${spacing(1)} [body-start] minmax(0, 1fr) [body-end];
            }

            // Header, Label, and Body
            :has(.wv-modal-header + .wv-modal-label + .wv-modal-flex-content):not(:has(> .wv-modal-footer)) {
              grid-template-rows:
                [header-start] auto [header-end] 0 [label-start] auto [label-end] ${spacing(1)} [body-start] minmax(
                  0,
                  1fr
                )
                [body-end];
            }

            // Header, Body, and Footer
            :has(.wv-modal-header + .wv-modal-flex-content + .wv-modal-footer):not(:has(> .wv-modal-label)) {
              grid-template-rows: [header-start] auto [header-end] ${spacing(1)} [body-start] minmax(0, 1fr) [body-end] ${spacing(
                  3
                )} [footer-start] auto [footer-end];
            }

            // Header, Label, Body, and Footer
            :has(.wv-modal-header + .wv-modal-label + .wv-modal-flex-content + .wv-modal-footer) {
              grid-template-rows:
                [header-start] auto [header-end] 0 [label-start] auto [label-end] ${spacing(1)} [body-start] minmax(
                  0,
                  1fr
                )
                [body-end] ${spacing(3)} [footer-start] auto [footer-end];
            }
          `
        : css`
            overflow-y: auto;
          `,
      maxWidth > 0 &&
        css`
          @media only screen and (min-width: ${maxWidth + 48}px) {
            width: auto;
          }
        `,
    ];
  };

  const styles = useMemo(() => {
    return getStyles();
  }, [isResponsiveMode, minWidth, maxWidth]);

  const props = {
    className: mergeClasses('wv-modal-box', className),
    css: [styles],
    onClick: (e) => e.stopPropagation(),
    ...rest,
    ...containerProps,
  };

  if (asChild) {
    const element = cloneElement(children as React.ReactElement, props);

    if (typeof element.type === 'string') {
      const Component = motion[element.type];
      return (
        <AnimatePresence>
          <Component
            {...element.props}
            className={mergeClasses('responsive-modal__box responsive-modal__box--element', element.props.className)}
          />
        </AnimatePresence>
      );
    }

    return <>{element}</>;
  }

  return (
    <AnimatePresence>
      <motion.section {...props} className={mergeClasses('responsive-modal__box', props.className)}>
        {children}
        <ContentLoader
          css={css`
            border-radius: ${borderRadius.medium};
          `}
          {...loadingState}
        />
      </motion.section>
    </AnimatePresence>
  );
};
